import { useState, useEffect, useCallback } from 'react'
import { useCampaignsList } from './useCampaigns'

const STORAGE_KEY = 'intone_selected_campaign_id'

/**
 * Hook for managing campaign selection with session storage persistence.
 * - Auto-selects the first campaign when campaigns load and no selection exists
 * - Persists selection to session storage
 * - Selection persists across page navigation
 */
export function useCampaignSelection() {
  const { data: campaigns = [], isLoading: campaignsLoading } = useCampaignsList()
  const [campaignId, setCampaignIdState] = useState<number | null>(() => {
    // Initialize from session storage
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = parseInt(stored, 10)
      return isNaN(parsed) ? null : parsed
    }
    return null
  })

  // When campaigns load, validate selection or auto-select first campaign
  useEffect(() => {
    if (campaignsLoading || campaigns.length === 0) return

    // Check if current selection is valid (exists in campaigns list)
    const isValidSelection = campaignId !== null && campaigns.some((c) => c.id === campaignId)

    if (!isValidSelection) {
      // Auto-select first campaign
      const firstCampaign = campaigns[0]
      if (firstCampaign) {
        setCampaignIdState(firstCampaign.id)
        sessionStorage.setItem(STORAGE_KEY, String(firstCampaign.id))
      }
    }
  }, [campaigns, campaignsLoading, campaignId])

  // Setter that also persists to session storage
  const setCampaignId = useCallback((id: number | null) => {
    setCampaignIdState(id)
    if (id !== null) {
      sessionStorage.setItem(STORAGE_KEY, String(id))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const selectedCampaign = campaigns.find(c => c.id === campaignId) ?? null

  return {
    campaignId,
    setCampaignId,
    campaigns,
    campaignsLoading,
    selectedCampaign,
  }
}
